import { useApolloClient } from '@apollo/client';
import { useEffect } from 'react';

import { useSubscribeToAiSettingsEvents } from '../aiSettingsEvents';
import { aiSettingsQueryNames } from '../utils/aiSettingsQueries';

/**
 * Keeps the settings on screen in step with changes made by other agents, as
 * the host app's web socket reports them.
 *
 * The events never say who acted, so an agent's own saves come back too, and
 * none carries the settings themselves. Each one just reads again whatever
 * settings are on screen; deciding what that means for unsaved edits is left
 * to the form.
 *
 * Without the host supplying events this does nothing, and the page is the
 * snapshot it was when it loaded.
 */
export function useAiSettingsLiveUpdates(): void {
  const subscribe = useSubscribeToAiSettingsEvents();
  const client = useApolloClient();

  useEffect(() => {
    if (!subscribe) return undefined;

    const refetch = () => {
      client
        .refetchQueries({
          // Only what is mounted: the versions list is not on every page.
          include: 'active',
          onQueryUpdated: (query) =>
            aiSettingsQueryNames.includes(query.queryName ?? ''),
        })
        // A read that fails leaves the page as stale as it would have been
        // without the event, which the next event or reconnect puts right.
        .catch(() => undefined);
    };

    return subscribe({
      onDraftUpdated: refetch,
      onVersionCreated: refetch,
      onVersionUpdated: refetch,
      onReconnected: refetch,
    });
  }, [subscribe, client]);
}
